import * as R from 'ramda'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { Profiler } from '@weblite/profiler'
import { getAquaManager } from '@weblite/aqua'
import { Timestamp } from '@weblite/ts-action/types'
import { Message } from '@weblite/ts-action/message'
import {
  useIsFirstMessageInChat,
  useOpenedRoom,
  useOpenedRoomFirstUnseenMessageIid,
  useOpenedRoomId,
  useOpenedRoomLoading,
  useOpenedRoomMessages,
  useOpenedRoomMessageToScroll,
} from '$logic/opened-room/react-hook'
import { PendingUpload } from '$logic/pending-uploads/types'
import { PendingMessage } from '$logic/pending-messages/types'
import { scrollToMessageByIid } from '$logic/opened-room'
import { openedRoomStorage } from '$logic/opened-room/opened-room.storage'
import { ROOM_MESSAGE_LIST_AQUA_MANAGER_ID } from '$comp/_app/components/chat/components/body/components/messageList/message-list'
import { useRoomLastMessage } from '$logic/room-last-message/react-hook'
import {
  useClientUserPermissions,
  useRoomSubscriptionDate,
} from '$logic/subscription/react-hook'
import { ProfilerAction } from '$logic/profiler'
import { useRoomStorageByRoomId } from '$logic/message/react-hook'

export const isInlineWappMessage = ({
  height,
  heightSlope,
}: {
  height?: number
  heightSlope?: number
} = {}) => !!(heightSlope || height)

export type PosInBlock = 'START' | 'MID' | 'END' | 'SINGLE'

export interface IInlineLoading {
  type: 'INLINE_LOADING'
}

export interface IDateSeparator {
  type: 'DATE_SEPARATOR'
  date: Timestamp
}

export interface IJoinedChannelEvent {
  type: 'JOINED_CHANNEL_EVENT'
  date: Timestamp
}

export interface IUnseenSeparator {
  type: 'UNSEEN_SEPARATOR'
}

export type MessageListItem = (
  | IDateSeparator
  | IUnseenSeparator
  | IJoinedChannelEvent
  | Message
  | PendingUpload
  | PendingMessage
  | OnboardingMessage
) & { posInBlock: PosInBlock; showSender: boolean; needVisibility?: true }

const createDateSeparator = (date?: Timestamp): IDateSeparator | undefined =>
  date
    ? {
        type: 'DATE_SEPARATOR',
        date,
      }
    : undefined

const createJoinedHallEvent = (
  date?: Timestamp,
): IJoinedChannelEvent | undefined =>
  date
    ? {
        type: 'JOINED_CHANNEL_EVENT',
        date,
      }
    : undefined

const createUnseenSeparator = (): IUnseenSeparator => ({
  type: 'UNSEEN_SEPARATOR',
})

export const haveDifferentDates = (firstDate: Date, secondDate: Date) =>
  firstDate.getDate() !== secondDate.getDate() ||
  firstDate.getMonth() !== secondDate.getMonth() ||
  firstDate.getFullYear() !== secondDate.getFullYear()

function decorateMessageWithJoinedChannelEvent({
  msg,
  hasPrevMsgs,
  previousMessageDate,
  subscriptionDate,
  lastMessage,
  firstVisibleIid,
}: {
  msg: any
  hasPrevMsgs: boolean
  firstVisibleIid: number
  previousMessageDate?: Timestamp
  subscriptionDate?: Timestamp
  lastMessage?: Message
}) {
  const msgDate = msg.date || msg.pendingDate
  const msgIid = msg.iid
  let joinedChannelMessage: IJoinedChannelEvent | undefined
  let subscriptionPositionRelativeToMsg: 'before' | 'after' | undefined

  if (msgDate && subscriptionDate) {
    const joinedBeforeFirstMessage =
      subscriptionDate < msgDate &&
      !hasPrevMsgs &&
      msgIid &&
      msgIid <= (firstVisibleIid || 1)

    const joinedAfterLastMessage =
      msg?.id === lastMessage?.id &&
      subscriptionDate > (lastMessage?.date || Infinity)

    const joinedBeforeCurrentMessage =
      previousMessageDate &&
      previousMessageDate < subscriptionDate &&
      subscriptionDate < msgDate

    if (
      joinedBeforeFirstMessage ||
      joinedBeforeCurrentMessage ||
      joinedAfterLastMessage
    ) {
      joinedChannelMessage = createJoinedHallEvent(subscriptionDate)
      subscriptionPositionRelativeToMsg = joinedAfterLastMessage
        ? 'after'
        : 'before'
    }
  }

  return !subscriptionPositionRelativeToMsg
    ? [msg]
    : subscriptionPositionRelativeToMsg === 'before'
      ? [joinedChannelMessage, msg]
      : [msg, joinedChannelMessage]
}

export const loadChatProfiler = new Profiler(ProfilerAction.LOAD_CHAT)

export function useOpenedRoomMessageList(): {
  loading: boolean
  messageList: MessageListItem[]
  lastVisibleMessage: Message | PendingMessage | PendingUpload | undefined
} {
  const loading = useOpenedRoomLoading()
  const messageToScroll = useOpenedRoomMessageToScroll()
  const firstUnseenIid = useOpenedRoomFirstUnseenMessageIid()
  const { messages: openedRoomMessages, lastVisibleMessage } =
    useOpenedRoomMessages()
  const roomId = useOpenedRoomId()
  const { value: subscriptionDate } = useRoomSubscriptionDate(roomId)
  const lastMessage = useRoomLastMessage(roomId)

  const { pv, privateSpace, subscription, wappRoom } =
    useRoomStorageByRoomId(roomId)

  const firstVisibleIid =
    pv?.firstVisibleIid ||
    privateSpace?.firstVisibleIid ||
    wappRoom?.firstVisibleIid ||
    subscription?.firstVisibleIid

  //  report load time to analytics
  useEffect(() => {
    if (!loading) {
      loadChatProfiler.stop({ roomId })
    }
  }, [loading, roomId])

  useLayoutEffect(() => {
    if (!messageToScroll) return
    const aquaManager = getAquaManager(ROOM_MESSAGE_LIST_AQUA_MANAGER_ID)
    requestAnimationFrame(async () => {
      if (typeof messageToScroll === 'string') aquaManager?.scrollToBottom(true)
      else {
        const { iid, ...scrollOptions } = messageToScroll
        await scrollToMessageByIid(iid, scrollOptions)
      }
      openedRoomStorage.setMessageToScroll(null)
    })
  }, [messageToScroll])

  const messagesWithExtraItems = useMemo(
    () =>
      loading
        ? []
        : R.compose(
            R.unnest,
            R.map((block: any[]) =>
              block.map((blockItem, index) => ({
                ...blockItem,
                showSender: index === 0,
                posInBlock:
                  block.length === 1
                    ? 'SINGLE'
                    : index === 0
                      ? 'START'
                      : index === block.length - 1
                        ? 'END'
                        : 'MID',
              })),
            ),
            R.groupWith(
              ({ sender: firstSender }, { sender: secondSender }) =>
                firstSender?.type === secondSender?.type &&
                firstSender?.id === secondSender?.id,
            ),
            R.reduce((acc, msg: any) => {
              const msgDate = msg.date || msg.pendingDate
              const previousMessageDate =
                R.last(acc)?.date || R.last(acc)?.pendingDate

              const messageWithJoinedEvent =
                decorateMessageWithJoinedChannelEvent({
                  msg,
                  subscriptionDate,
                  previousMessageDate,
                  hasPrevMsgs: R.length(acc) > 0,
                  lastMessage,
                  firstVisibleIid,
                })

              const message =
                firstUnseenIid === msg.iid
                  ? [createUnseenSeparator(), ...messageWithJoinedEvent]
                  : messageWithJoinedEvent

              if (!R.length(acc))
                return [msgDate && createDateSeparator(msgDate), ...message]

              return R.concat(
                acc,
                previousMessageDate &&
                  msgDate &&
                  haveDifferentDates(
                    new Date(previousMessageDate),
                    new Date(msgDate),
                  )
                  ? [createDateSeparator(msgDate), ...message]
                  : message,
              )
            }, []),
            R.reject(R.path(['patches', 'deleted'])),
          )(openedRoomMessages),
    [
      firstUnseenIid,
      openedRoomMessages,
      loading,
      subscriptionDate,
      lastMessage,
      firstVisibleIid,
    ],
  )

  return {
    loading,
    messageList: messagesWithExtraItems,
    lastVisibleMessage,
  }
}

export enum OnboardingMessageTypes {
  CHANNEL = 'CHANNEL_ONBOARDING',
  GROUP = 'GROUP_ONBOARDING',
  PV = 'PV_ONBOARDING',
}

export type OnboardingMessage = {
  type: 'ONBOARDING'
  withSeparator?: boolean
  onboardingType: OnboardingMessageTypes
}

export function useOpenedRoomOnboardingMessage(
  messageList: any[],
  messageListLoading: boolean,
) {
  const { openedRoom } = useOpenedRoom()

  const isFirstMessageInChat = useIsFirstMessageInChat(openedRoom?.id || '')

  const isPeerJustSignedUp = useMemo(() => {
    const secondMessage = messageList[1]
    return (
      messageList.length === 2 &&
      secondMessage?.type === 'EVENT' &&
      secondMessage?.attachment?.event === 'SIGNUP_CONTACT'
    )
  }, [messageList])

  const permissions = useClientUserPermissions(openedRoom?.id || '')
  const isAdmin = permissions?.isAdmin || permissions?.isOwner

  const shouldRenderAboveChatList =
    isFirstMessageInChat && !(openedRoom?.type === 'PV' && !isPeerJustSignedUp)

  const type = 'ONBOARDING'

  return useMemo<{
    onboardingMessage: OnboardingMessage[]
    shouldRenderAboveChatList: boolean
  }>(() => {
    const withSeparator = messageList.length > 0

    if (!openedRoom || messageListLoading)
      return {
        onboardingMessage: [],
        shouldRenderAboveChatList: shouldRenderAboveChatList,
      }
    if (openedRoom.type === 'CHANNEL' && isAdmin)
      return {
        onboardingMessage: [
          {
            type,
            onboardingType: OnboardingMessageTypes.CHANNEL,
            withSeparator,
          },
        ],
        shouldRenderAboveChatList,
      }
    if (openedRoom.type === 'GROUP' && isAdmin)
      return {
        onboardingMessage: [
          {
            type,
            onboardingType: OnboardingMessageTypes.GROUP,
            withSeparator,
          },
        ],
        shouldRenderAboveChatList,
      }
    if (openedRoom.type === 'PV')
      return {
        onboardingMessage: [
          { type, onboardingType: OnboardingMessageTypes.PV, withSeparator },
        ],
        shouldRenderAboveChatList,
      }

    return {
      onboardingMessage: [],
      shouldRenderAboveChatList: shouldRenderAboveChatList,
    }
  }, [openedRoom, isAdmin, messageListLoading, messageList])
}
